import { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { loginWithGoogle, checkTokenValidity } from '../services/authService';

export const useAuth = () => {
    const [googleUser, setUser] = useState(null);
    const [accessToken, setAccessToken] = useState(null);

    // Restore session from localStorage
    useEffect(() => {
        const storedToken = localStorage.getItem("googleAccessToken");
        const storedUser = localStorage.getItem("googleUser");

        if (storedToken && storedUser && storedToken !== 'undefined') {
            try {
                const parsedUser = JSON.parse(storedUser);
                if (parsedUser && parsedUser.name && parsedUser.email) {
                    setAccessToken(storedToken);
                    setUser(parsedUser);
                    console.log('✅ Session restored');
                } else {
                    localStorage.removeItem('googleUser');
                    localStorage.removeItem('googleAccessToken');
                }
            } catch (error) {
                console.error('Error parsing stored user:', error);
                localStorage.removeItem('googleUser');
                localStorage.removeItem('googleAccessToken');
            }
        }
    }, []);

    // Listen for Firebase auth state changes to handle token refresh
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                // Firebase automatically refreshes the ID token
                // Re-fetch access token when auth state changes
                const storedToken = localStorage.getItem("googleAccessToken");
                if (storedToken) {
                    const isValid = await checkTokenValidity(storedToken);
                    if (!isValid) {
                        console.log('⚠️ Access token expired, user needs to re-login');
                        logout();
                    }
                }
            }
        });

        return () => unsubscribe();
    }, []);

    // Check token validity every 10 minutes
    useEffect(() => {
        if (!accessToken) return;

        const checkAndRefresh = async () => {
            const isValid = await checkTokenValidity(accessToken);
            if (!isValid) {
                console.log('⚠️ Token expired, logging out...');
                logout();
            }
        };

        const interval = setInterval(checkAndRefresh, 10 * 60 * 1000);
        return () => clearInterval(interval);
    }, [accessToken]);

    const login = async () => {
        try {
            const { accessToken: newToken, userData } = await loginWithGoogle();

            setAccessToken(newToken);
            setUser(userData);

            localStorage.setItem("googleAccessToken", newToken);
            localStorage.setItem("googleUser", JSON.stringify(userData));

            console.log('✅ Login complete!');
        } catch (error) {
            console.error('Error during login:', error);
            alert('Login failed. Please try again.');
        }
    };

    const logout = async () => {
        try {
            await signOut(auth);
        } catch (error) {
            console.error('Error signing out of Firebase:', error);
        }

        setUser(null);
        setAccessToken(null);

        localStorage.removeItem("googleAccessToken");
        localStorage.removeItem("googleRefreshToken");
        localStorage.removeItem("googleUser");
        localStorage.removeItem("classes");
        localStorage.removeItem("classOrganization");

        alert("Successfully logged out!");
    };

    return { googleUser, accessToken, login, logout };
};